<style lang="scss">
.login-box {
    border: red;
    width: 360px;
    margin: 7% auto;
}

.login-box-body {
    border: red;
    background: #fff;
    padding: 20px;
    border-top: 0;
    color: #666;
}

.login-logo {
    font-size: 38px;
    text-align: center;
    margin-bottom: 25px;
    font-weight: bold;
    color: #444;
}

.form-group {
    margin-bottom: 30px;
}

.twofa-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.twofa-title {
    margin: 0;
    color: #444;
    font-size: 1.2rem;
}

.twofa-subtitle {
    margin: 0;
    color: #666;
    line-height: 1.4;
}

.twofa-qr-wrap {
    display: flex;
    justify-content: center;
    margin: 4px 0;
}

.twofa-qr {
    max-width: 220px;
    width: 100%;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 8px;
    background: #fff;
}

.twofa-secret {
    font-family: monospace;
    letter-spacing: 0.06em;
    color: #444;
    padding: 8px 10px;
    background: #f5f6f8;
    border: 1px solid #e3e5e8;
    border-radius: 6px;
    word-break: break-all;
}

.twofa-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.twofa-actions > * {
    align-self: center;
}

.twofa-error {
    margin: 0;
    color: #b00020;
    font-size: 0.95rem;
}

.twofa-help {
    margin: 6px 0 0;
    font-size: 0.9rem;
    color: #666;
    line-height: 1.4;
}

.recovery-codes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 6px 0;
}

.recovery-code {
    font-family: monospace;
    font-size: 0.92rem;
    color: #444;
    padding: 8px;
    background: #f5f6f8;
    border: 1px solid #e3e5e8;
    border-radius: 6px;
    text-align: center;
}
</style>

<template>
    <div class="login-box">
        <div class="login-logo">T-Star</div>
        <div class="login-box-body">
            <form
                id="frmLoginId"
                class="login-form"
                v-if="loginPhase === 'credentials'"
            >
                <div class="form-group">
                    <InputBox
                        id="ibEmail"
                        v-model.trim="email"
                        autocomplete="email"
                        @keydown.enter="login"
                        type="email"
                        :placeholder="$t('email')"
                    />
                </div>
                <div class="form-group">
                    <InputBox
                        id="ibPassword"
                        v-model="password"
                        autocomplete="current-password"
                        @keydown.enter="login"
                        type="password"
                        :placeholder="$t('password')"
                    />
                </div>
            </form>

            <div class="twofa-panel" v-else-if="loginPhase === 'enroll'">
                <h3 class="twofa-title">{{ $t('2faSetup') }}</h3>
                <p class="twofa-subtitle">
                    {{ $t('scanQrCode') }}
                </p>

                <div class="twofa-qr-wrap" v-if="totpQrDataUrl">
                    <img
                        class="twofa-qr"
                        :src="totpQrDataUrl"
                        :alt="$t('totpQrCodeAlt')"
                    />
                </div>

                <div class="twofa-secret">{{ totpSecret }}</div>

                <InputBox
                    ref="enrollTotpInput"
                    id="ibEnrollTotpCode"
                    v-model.trim="totpCode"
                    @keydown.enter="confirmEnrollment"
                    type="text"
                    maxlength="6"
                    :placeholder="$t('enter2faCode')"
                />

                <p class="twofa-error" v-if="twoFaError">{{ twoFaError }}</p>

                <div class="twofa-actions">
                    <Button @click="confirmEnrollment" :disabled="twoFaBusy">{{
                        $t('verifyContinue')
                    }}</Button>
                    <Button
                        outline
                        @click="cancelTwoFa"
                        :disabled="twoFaBusy"
                        >{{ $t('cancel') }}</Button
                    >
                </div>
            </div>

            <div class="twofa-panel" v-else-if="loginPhase === 'verify'">
                <h3 class="twofa-title">{{ $t('2faVerification') }}</h3>
                <p class="twofa-subtitle" v-if="!useRecoveryCodeMode">
                    {{ $t('enter2faCodeApp') }}
                </p>
                <p class="twofa-subtitle" v-else>
                    {{ $t('enterRecoverCode') }}
                </p>

                <InputBox
                    ref="verifyTotpInput"
                    id="ibVerifyTotpCode"
                    v-model.trim="totpCode"
                    @keydown.enter="verifyTwoFa"
                    type="text"
                    :maxlength="useRecoveryCodeMode ? 14 : 6"
                    :placeholder="
                        useRecoveryCodeMode
                            ? 'ABCD-EFGH-IJKL'
                            : $t('enter2faCode')
                    "
                />

                <p class="twofa-error" v-if="twoFaError">{{ twoFaError }}</p>

                <div class="twofa-actions">
                    <Button @click="verifyTwoFa" :disabled="twoFaBusy">{{
                        useRecoveryCodeMode
                            ? $t('useRecoveryCode')
                            : $t('verify')
                    }}</Button>
                    <Button
                        v-if="canUseRecoveryCode"
                        outline
                        @click="toggleRecoveryCodeMode"
                        :disabled="twoFaBusy"
                    >
                        {{
                            useRecoveryCodeMode
                                ? $t('useAuthenticatorCode')
                                : $t('useRecoveryCode')
                        }}
                    </Button>
                    <Button
                        outline
                        @click="cancelTwoFa"
                        :disabled="twoFaBusy"
                        >{{ $t('cancel') }}</Button
                    >
                </div>
            </div>

            <div class="twofa-panel" v-else-if="loginPhase === 'recovery'">
                <h3 class="twofa-title">{{ $t('saveRecoveryCodes') }}</h3>
                <p class="twofa-subtitle">
                    {{ $t('saveRecoveryCodesSubtitle') }}
                </p>

                <div class="recovery-codes">
                    <div
                        class="recovery-code"
                        v-for="code in recoveryCodes"
                        :key="code"
                    >
                        {{ code }}
                    </div>
                </div>

                <div class="twofa-actions">
                    <Button @click="copyRecoveryCodes">{{
                        $t('copyRecoveryCodes')
                    }}</Button>
                    <Button outline @click="downloadRecoveryCodes"
                        >Download.txt</Button
                    >
                </div>

                <div class="twofa-actions">
                    <Button @click="finishRecoverySetup">{{
                        $t('continue')
                    }}</Button>
                </div>
            </div>

            <Button @click="login" v-if="loginPhase === 'credentials'">{{
                $t('login')
            }}</Button>
        </div>
    </div>
</template>

<script>
import * as toastr from 'toastr';
import QRCode from 'qrcode';
import * as auth from '../api/auth';

export default {
    watch: {
        loginPhase(nextPhase) {
            if (nextPhase === 'enroll' || nextPhase === 'verify') {
                this.focusTwoFaInput();
            }
        },
        useRecoveryCodeMode() {
            if (this.loginPhase === 'verify') {
                this.focusTwoFaInput();
            }
        },
    },
    data() {
        return {
            email: '',
            password: '',
            isSubmitting: false,
            loginPhase: 'credentials',
            twoFaBusy: false,
            twoFaError: '',
            totpCode: '',
            totpSecret: '',
            totpQrDataUrl: '',
            pendingAuthToken: '',
            postTwoFaToken: '',
            recoveryCodes: [],
            useRecoveryCodeMode: false,
            canUseRecoveryCode: false,
        };
    },
    methods: {
        focusTwoFaInput() {
            this.$nextTick(() => {
                let inputRef = null;
                if (this.loginPhase === 'enroll') {
                    inputRef = this.$refs.enrollTotpInput;
                } else if (this.loginPhase === 'verify') {
                    inputRef = this.$refs.verifyTotpInput;
                }

                if (inputRef && typeof inputRef.setFocus === 'function') {
                    inputRef.setFocus();
                }
            });
        },

        async beginTwoFaFlow(token, loginPayload) {
            this.twoFaError = '';
            this.totpCode = '';
            this.pendingAuthToken = token;
            this.postTwoFaToken = '';
            this.recoveryCodes = [];
            this.useRecoveryCodeMode = false;
            this.canUseRecoveryCode =
                loginPayload?.allow_recovery_code === true;

            const isTotpRequired = loginPayload?.requires_totp === true;
            const hasTotpSecret = loginPayload?.totp_secret_configured === true;

            // 2FA not required for this account.
            if (!isTotpRequired) {
                auth.completeLogin(this.pendingAuthToken);
                this.resetTwoFaState(false);
                return;
            }

            // 2FA required and already provisioned.
            if (hasTotpSecret) {
                this.loginPhase = 'verify';
                this.focusTwoFaInput();
                return;
            }

            // 2FA required but not yet provisioned.
            this.loginPhase = 'enroll';
            const provision = await auth.provisionTotp(token);
            if (
                provision.httpStatus !== 200 ||
                !provision.payload?.otpauth_uri
            ) {
                this.twoFaError = this.$t('unableToLoadTwoFaSetupInformation');
                return;
            }

            this.totpSecret = provision.payload.totp_secret || '';

            try {
                this.totpQrDataUrl = await QRCode.toDataURL(
                    provision.payload.otpauth_uri,
                    {
                        width: 220,
                        margin: 1,
                    },
                );
            } catch (error) {
                this.totpQrDataUrl = '';
                console.error(this.$t('failedToGenerateTotpQrCode'), error);
            }

            this.focusTwoFaInput();
        },

        isTotpCodeFormatValid() {
            return /^\d{6}$/.test(this.totpCode || '');
        },

        async confirmEnrollment() {
            await this.verifyTwoFa();
        },

        async verifyTwoFa() {
            if (this.twoFaBusy) {
                return;
            }

            this.twoFaBusy = true;
            this.twoFaError = '';

            try {
                let result;
                if (this.useRecoveryCodeMode) {
                    if (!this.canUseRecoveryCode) {
                        this.twoFaError = this.$t(
                            'recoveryCodeLoginNotAvailable',
                        );
                        return;
                    }

                    if (!(this.totpCode || '').trim()) {
                        this.twoFaError = this.$t('enterRecoveryCode');
                        return;
                    }

                    result = await auth.verifyRecoveryCode(
                        this.pendingAuthToken,
                        this.totpCode,
                    );
                } else {
                    if (!this.isTotpCodeFormatValid()) {
                        this.twoFaError = this.$t('enterValid6DigitCode');
                        return;
                    }

                    result = await auth.verifyTotp(
                        this.pendingAuthToken,
                        this.totpCode,
                        this.loginPhase === 'enroll' ? this.totpSecret : '',
                    );
                }

                if (result.httpStatus !== 200 || !result?.payload?.token) {
                    if (result?.msg === 'tooManyTwoFaAttempts') {
                        this.twoFaError = this.$t('tooManyTwoFaAttempts');
                    } else if (result?.msg === 'totpCodeReused') {
                        this.twoFaError = this.$t('totpCodeReused');
                    } else if (result?.msg === 'totpClockSkew') {
                        this.twoFaError = this.$t('totpClockSkew');
                    } else if (
                        result?.msg === 'invalidRecoveryCode' ||
                        result?.msg === 'recoveryCodeAlreadyUsed'
                    ) {
                        this.twoFaError = this.$t('invalidRecoveryCode');
                    } else if (result?.msg === 'noRecoveryCodesAvailable') {
                        this.twoFaError = this.$t('noRecoveryCodesAvailable');
                    } else if (result?.msg === 'invalidTotpSecret') {
                        this.twoFaError = this.$t('invalidTotpSecret');
                    } else {
                        this.twoFaError = this.$t('invalidVerificationCode');
                    }
                    return;
                }

                const recoveryCodes = Array.isArray(
                    result?.payload?.recovery_codes,
                )
                    ? result.payload.recovery_codes
                    : [];

                if (recoveryCodes.length > 0) {
                    this.recoveryCodes = recoveryCodes;
                    this.postTwoFaToken = result.payload.token;
                    this.loginPhase = 'recovery';
                    toastr.success(this.$t('recoveryCodesNotice'));
                    return;
                }

                auth.completeLogin(result.payload.token);
                toastr.success(this.$t('2faVerificationSucceeded'));
                this.resetTwoFaState(false);
            } finally {
                this.twoFaBusy = false;
            }
        },

        toggleRecoveryCodeMode() {
            if (!this.canUseRecoveryCode) {
                return;
            }

            this.useRecoveryCodeMode = !this.useRecoveryCodeMode;
            this.twoFaError = '';
            this.totpCode = '';
        },

        async copyRecoveryCodes() {
            const payload = this.recoveryCodes.join('\n');
            try {
                await navigator.clipboard.writeText(payload);
                toastr.success(this.$t('recoveryCodesCopied'));
            } catch {
                toastr.error(this.$t('unableToCopyRecoveryCodes'));
            }
        },

        downloadRecoveryCodes() {
            const lines = [
                this.$t('tstarRecoveryCodes'),
                this.$t('generated', { date: new Date().toISOString() }),
                '',
                ...this.recoveryCodes,
            ];

            const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'tstar-recovery-codes.txt';
            link.click();
            URL.revokeObjectURL(url);
            toastr.success(this.$t('recoveryCodeFileDownloaded'));
        },

        finishRecoverySetup() {
            if (!this.postTwoFaToken) {
                toastr.error(this.$t('unableToFinalizeLogin'));
                return;
            }

            auth.completeLogin(this.postTwoFaToken);
            toastr.success(this.$t('loginCompleted'));
            this.resetTwoFaState(false);
        },

        cancelTwoFa() {
            this.resetTwoFaState(true);
        },

        resetTwoFaState(revokePendingToken) {
            if (revokePendingToken) {
                auth.logoutPendingToken(this.pendingAuthToken);
                if (
                    this.postTwoFaToken &&
                    this.postTwoFaToken !== this.pendingAuthToken
                ) {
                    auth.logoutPendingToken(this.postTwoFaToken);
                }
            }

            this.loginPhase = 'credentials';
            this.twoFaError = '';
            this.totpCode = '';
            this.totpSecret = '';
            this.totpQrDataUrl = '';
            this.pendingAuthToken = '';
            this.postTwoFaToken = '';
            this.recoveryCodes = [];
            this.useRecoveryCodeMode = false;
            this.canUseRecoveryCode = false;
        },

        async login() {
            if (this.isSubmitting) {
                return;
            }

            this.isSubmitting = true;
            toastr.remove();
            try {
                const result = await auth.login(this.email, this.password);
                const httpStatus = result?.httpStatus;

                if (httpStatus === 401) {
                    toastr.error(
                        this.$t('invalidEmailOrPassword'),
                        this.$t('loginFailed'),
                    );
                    return;
                } else if (httpStatus !== 200) {
                    toastr.error(
                        this.$t('serverUnavailable'),
                        this.$t('serverError'),
                    );
                    return;
                }

                const token = result?.payload?.token;
                if (!token) {
                    toastr.error(
                        this.$t('serverUnavailable'),
                        this.$t('serverError'),
                    );
                    return;
                }

                await this.beginTwoFaFlow(token, result.payload);
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>
